import { createContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { apiRequest } from "../config/api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const navigate = useNavigate();

  const clearAuthData = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  }, []);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (session?.user && !error) {
          try {
            const userData = await apiRequest(`/auth/user/${session.user.id}`);

            if (!mounted) return;

            if (userData.success && userData.user) {
              setUser({
                email: session.user.email,
                id: session.user.id,
                name: userData.user.name || null,
                enterprise_id: userData.user.enterprise_id || null,
                enterprise_role: userData.user.enterprise_role || null,
              });
            } else {
              setUser({
                email: session.user.email,
                id: session.user.id,
                name: null,
                enterprise_id: null,
                enterprise_role: null,
              });
            }
          } catch (dbError) {
            setUser({
              email: session.user.email,
              id: session.user.id,
              name: null,
              enterprise_id: null,
              enterprise_role: null,
            });
          }

          setSession(session);
        } else {
          setUser(null);
          setSession(null);
        }
      } catch (error) {
        if (mounted) {
          setUser(null);
          setSession(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listener para mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      if (event === "SIGNED_OUT" || !session) {
        setUser(null);
        setSession(null);
      } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Para eventos de login, vamos fazer uma busca rápida dos dados
        setUser({
          email: session.user.email,
          id: session.user.id,
          name: null,
          enterprise_id: null,
          enterprise_role: null,
        });
        setSession(session);

        // Busca dados extras em background com timeout
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout')), 10000)
        );

        const userDataPromise = apiRequest(`/auth/user/${session.user.id}`);

        Promise.race([userDataPromise, timeoutPromise])
          .then((userData) => {
            if (mounted && userData.success && userData.user) {
              setUser((prev) => ({
                ...prev,
                name: userData.user.name,
                enterprise_id: userData.user.enterprise_id,
                enterprise_role: userData.user.enterprise_role,
              }));
            } else {
              console.warn('Erro ao buscar dados do usuário');
            }
          })
          .catch((error) => {
            if (mounted) {
              console.warn('Erro ou timeout ao buscar dados do usuário:', error);
              // Mantém o usuário básico mesmo com erro
            }
          });
      }

      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signin = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { success: false, message: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, message: "Erro durante o login" };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      // Log persistente no localStorage para debug
      const debugLog = (msg, data) => {
        const logs = JSON.parse(localStorage.getItem('google_login_logs') || '[]');
        logs.push({
          timestamp: new Date().toISOString(),
          message: msg,
          data: data
        });
        localStorage.setItem('google_login_logs', JSON.stringify(logs));
      };

      debugLog('Iniciando login com Google', {
        origin: window.location.origin,
        redirectTo: `${window.location.origin}/home`
      });

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/home`
        }
      });

      debugLog('Resposta do Supabase OAuth', {
        hasData: !!data,
        hasError: !!error,
        errorMessage: error?.message,
        errorDetails: error
      });

      if (error) {
        debugLog('Erro no OAuth', error);
        return { success: false, message: error.message };
      }

      debugLog('OAuth iniciado com sucesso', data);

      // Redireciona para a URL do Google OAuth
      if (data?.url) {
        debugLog('Redirecionando para Google', { url: data.url });
        window.location.href = data.url;
      }

      return { success: true, url: data?.url };
    } catch (error) {
      const debugLog = (msg, data) => {
        const logs = JSON.parse(localStorage.getItem('google_login_logs') || '[]');
        logs.push({
          timestamp: new Date().toISOString(),
          message: msg,
          data: data
        });
        localStorage.setItem('google_login_logs', JSON.stringify(logs));
        console.error('❌ [Google Login]', msg, data);
      };

      debugLog('Exceção capturada', {
        message: error.message,
        stack: error.stack,
        error: error
      });

      return { success: false, message: "Erro durante o login com Google: " + error.message };
    }
  }, []);

  const signup = useCallback(async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        return { success: false, message: error.message };
      }

      return { success: true, user: data.user };
    } catch (error) {
      console.error("Signup error:", error);
      return { success: false, message: "Erro ao criar conta" };
    }
  }, []);

  const signout = useCallback(async () => {
    try {
      await clearAuthData();
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Signout error:", error);
    }
  }, [clearAuthData, navigate]);

  const getAccessToken = useCallback(() => {
    return session?.access_token;
  }, [session]);

  const contextValue = {
    user,
    signed: !!user,
    loading,
    session,
    signin,
    signInWithGoogle,
    signup,
    signout,
    getAccessToken,
    clearAuthData,
  };


  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
